import React, { useEffect, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { buildUnauthorizedUrl, isDevLoginSessionLossCode, signedFetch } from '../super-picker/services/api';
import ProjectEssentialLayout from './components/ProjectEssentialLayout';
import Dashboard from './pages/Dashboard';
import ProjectDetail from './pages/ProjectDetail';
import HighlightsAndEbookPage from './pages/HighlightsAndEbookPage';
import ProjectEdmPage from './pages/ProjectEdmPage';
import ProjectsListingPage from './pages/ProjectsListingPage';
import SeminarsAndEventsPage from './pages/SeminarsAndEventsPage';
import ProjectCalendarPage from './pages/ProjectCalendarPage';
import ProjectVideosPage from './pages/ProjectVideosPage';
import GlsLandSaleDetailPage from './pages/GlsLandSaleDetailPage';
import SearchResultsPage from './pages/SearchResultsPage';

import { initAnalytics, setAnalyticsCea } from './services/analytics';
import { primeProjectEssentialUser } from './services/api';
import { PeEmbedProvider } from './embedContext';

type LoginUser = { Name?: string; Photo?: string; CEANumber?: string };

/**
 * Deep-link targets for htns.sg/project-essential?redirect=<key>&...
 * SSO forwards only the params listed in LoginApp's SSO_FORWARD_QUERY_PARAMS.
 */
const REDIRECT_TARGETS: Record<string, (params: URLSearchParams) => string | null> = {
  details: (params) => {
    const id = params.get('projectId')?.trim();
    return id ? `/project-essential/projects/${encodeURIComponent(id)}` : null;
  },
};

export function ProjectEssentialApp() {
  const navigate = useNavigate();
  const [accessState, setAccessState] = useState<'checking' | 'granted' | 'denied'>('checking');
  const [loginUser, setLoginUser] = useState<LoginUser | null>(null);

  useEffect(() => {
    if (accessState !== 'granted') return;
    const params = new URLSearchParams(window.location.search);
    const key = params.get('redirect')?.trim();
    const target = key ? REDIRECT_TARGETS[key]?.(params) : null;
    if (target) navigate(target, { replace: true });
  }, [accessState, navigate]);

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    let mounted = true;

    const verifyAccess = async () => {
      try {
        const response = await signedFetch('/api/project-essential/user/me');
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const errorCode = typeof data?.code === 'string' ? data.code : undefined;
          const devLoginRedirectHandled = response.status === 401 && isDevLoginSessionLossCode(errorCode);
          if (mounted) {
            setAccessState('denied');
            if (!devLoginRedirectHandled) {
              window.location.replace(
                buildUnauthorizedUrl(errorCode || 'NO_PROJECT_ESSENTIAL_ACCESS', { app: 'project-essential' })
              );
            }
          }
          return;
        }
        primeProjectEssentialUser(data);
        if (mounted) {
          setAnalyticsCea(data.CEANumber || data.ceaNumber);
          setAccessState('granted');
          setLoginUser({
            Name: data.name ?? data.Name,
            Photo: data.photo ?? data.Photo,
            CEANumber: data.CEANumber ?? data.ceaNumber,
          });
        }
      } catch {
        if (mounted) {
          setAccessState('denied');
          window.location.replace(
            buildUnauthorizedUrl('NO_PROJECT_ESSENTIAL_ACCESS', { app: 'project-essential' })
          );
        }
      }
    };

    verifyAccess();
    return () => { mounted = false; };
  }, []);

  if (accessState === 'checking') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2 }}>
        <CircularProgress />
        <Typography color="text.secondary">Checking Project Essentials access...</Typography>
      </Box>
    );
  }

  if (accessState === 'denied') {
    return null;
  }

  return (
    <PeEmbedProvider>
      <ProjectEssentialLayout loginUser={loginUser}>
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="search" element={<SearchResultsPage />} />
          <Route path="projects/:id" element={<ProjectDetail />} />
          <Route path="gls-land-sales/:id" element={<GlsLandSaleDetailPage />} />
          <Route path="highlights-ebook" element={<HighlightsAndEbookPage />} />
          <Route path="project-edm" element={<ProjectEdmPage />} />
          <Route path="upcoming-projects" element={<ProjectsListingPage variant="upcoming" />} />
          <Route path="all-projects" element={<ProjectsListingPage variant="all-projects" />} />
          <Route path="commercial-industrial" element={<ProjectsListingPage variant="commercial" />} />
          <Route path="international" element={<ProjectsListingPage variant="international" />} />
          <Route path="seminars-events" element={<SeminarsAndEventsPage />} />
          <Route path="project-calendar" element={<ProjectCalendarPage />} />
          <Route path="videos" element={<ProjectVideosPage />} />
          <Route path="*" element={<Dashboard />} />
        </Routes>
      </ProjectEssentialLayout>
    </PeEmbedProvider>
  );
}
